import { useContext, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import classes from './index.module.css';

import AccountToggle from 'Components/Menu/DesktopMenu/AccountToggle/AccountToggle';
import OnBehalfOfUserToggle from 'Components/Menu/DesktopMenu/OnBehalfOfUserToggle/OnBehalfOfUserToggle';

import { ReactComponent as Approvals } from 'Components/UI_Library/Icons/MobileIcons/Approvals.svg';
import { ReactComponent as Camera } from 'Components/UI_Library/Icons/MobileIcons/Camera.svg';
import { ReactComponent as Card } from 'Components/UI_Library/Icons/MobileIcons/Card.svg';
import { ReactComponent as Conversion } from 'Components/UI_Library/Icons/MobileIcons/Conversion.svg';
import { ReactComponent as Dashboard } from 'Components/UI_Library/Icons/MobileIcons/Dashboard.svg';
import { ReactComponent as Expenses } from 'Components/UI_Library/Icons/MobileIcons/Expenses.svg';
import { ReactComponent as Bills } from 'Components/UI_Library/Icons/MobileIcons/Bills.svg';
import { ReactComponent as Gift } from 'Components/UI_Library/Icons/MobileIcons/Gift.svg';
import { ReactComponent as Help } from 'Components/UI_Library/Icons/MobileIcons/Help.svg';
import { ReactComponent as Payments } from 'Components/UI_Library/Icons/MobileIcons/Payments.svg';
import { ReactComponent as Settings } from 'Components/UI_Library/Icons/MobileIcons/Settings.svg';
import { ReactComponent as Team } from 'Components/UI_Library/Icons/MobileIcons/Team.svg';
import { ReactComponent as Deposit } from 'Components/UI_Library/Icons/MobileIcons/Deposit.svg';
import { ReactComponent as Reporting } from 'Components/UI_Library/Icons/MobileIcons/Reporting.svg';
import { ReactComponent as Accounts } from 'Components/UI_Library/Icons/MobileIcons/Accounts.svg';
import { ReactComponent as Budget } from 'Components/UI_Library/Icons/MobileIcons/Budgets.svg';
import { ReactComponent as PaytronLogo } from 'Components/UI_Library/Icons/Paytron.svg';
import Prism from 'Components/UI_Library/Prism/Prism';
import { API as api } from 'Backend';
import { cardsHomeUrl } from 'Components/CardsGPS/utils/constants';
import { getCurrentUserId, getPermissions } from 'Lib/Utils';
import { isOktaInternalAuthToken } from 'Lib/Utils/authToken';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { NotificationsContext } from 'Components/Notifications';
import { planMap } from 'Components/Setup/Billing/utils/Utils';
import { useGetCompany } from 'Backend/company/getCompany';

const platform = Capacitor.getPlatform();
const isMobileApp = platform === 'ios' || platform === 'android';

const MobileMenu = () => {
    const notifications = useContext(NotificationsContext);
    const [isActive, setActive] = useState(false);
    const location = useLocation();
    const [baseCurrency, setbaseCurrency] = useState('');
    const [approvalsBadge, setApprovalsBadge] = useState('');
    const [appVersion, setappVersion] = useState('');
    const companyManager = getPermissions()?.permissions?.company?.manage;
    const isBusinessAccountPlan = notifications?.subscription?.planId === planMap('Business Account');
    const isFenix = isOktaInternalAuthToken();

    // COMPANY
    const { data: company, isSuccess: companyLoaded } = useGetCompany();

    // GET COMPANY BASE CURRENCY
    useEffect(() => {
        if (companyLoaded) {
            setbaseCurrency(company.baseCurrency);
        }
    }, [companyLoaded]);

    // GET APPROVALS
    const getApprovals = async () => {
        const res = await api.getApprovalRecords();
        const pendingApprovals = res?.data?.data?.length;
        const pendingApprovalsMore = res?.data?.cursor;
        if (pendingApprovals) {
            setApprovalsBadge(`${pendingApprovals}${pendingApprovalsMore ? '+' : ''}`);
        } else {
            setApprovalsBadge('');
        }
    };

    // GET APP VERSION
    useEffect(() => {
        (async () => {
            if (isMobileApp) {
                setappVersion((await App.getInfo())?.version || '');
            }
        })();
    }, []);

    useEffect(() => {
        getApprovals();
    }, [location.pathname]);

    const toggleClass = (off) => {
        if (off === true) {
            setActive(false);
            return;
        }
        setActive(!isActive);
    };

    const hasActiveClass = (path) => (location?.pathname?.includes(path) ? classes.active : '');

    const renderNavLink = ({ path, Component, title }) => (
        <NavLink onClick={() => toggleClass(true)} className={`${classes.mLink} ${hasActiveClass(path)}`} to={path}>
            <div className={classes.menuImage}>
                {approvalsBadge && path === '/approvals/pending' && (
                    <span className={classes.notificationBadge}>{approvalsBadge}</span>
                )}
                {Component}
                <span className={classes.pageTitle}>{title}</span>
            </div>
        </NavLink>
    );

    const renderMenuItem = ({ path, Component, label }) => (
        <div className={classes.menuItem}>
            <NavLink onClick={toggleClass} to={path} className={`${classes.menuLink} ${hasActiveClass(path)}`}>
                <div className={classes.menuImage}>{Component}</div>
                {label}
            </NavLink>
        </div>
    );

    if (isBusinessAccountPlan)
        return (
            <div className={classes.hideMobile}>
                <div className={isActive ? classes.backgroundOpen : ''} onClick={toggleClass}></div>
                <div className={`${classes.mobileMenu} ${isActive ? classes.fullMenuOpen : ''}`}>
                    {renderNavLink({ path: '/dashboard', Component: <Dashboard />, title: 'Dashboard' })}
                    {renderNavLink({ path: '/accounts', Component: <Accounts />, title: 'Accounts' })}
                    {renderNavLink({ path: '/payments', Component: <Payments />, title: 'Payments' })}
                    {renderNavLink({
                        path: `${cardsHomeUrl}/user/${getCurrentUserId()}`,
                        Component: <Card />,
                        title: 'Cards',
                    })}

                    <div className={classes.mLink}></div>
                </div>
                <div className={`${classes.menu} ${isActive ? classes.open : ''}`}>
                    <div className={`${classes.menuOpenButton} `} onClick={toggleClass}>
                        <span className={`${classes.lines} ${classes.line1}`}></span>
                        <span className={`${classes.lines} ${classes.line2}`}></span>
                        <span className={`${classes.lines} ${classes.line3}`}></span>
                    </div>
                    <div className={classes.menuItems}>
                        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                            <PaytronLogo fill="#123688" width={'100px'} style={{ marginLeft: '20px' }} />
                            <div className={classes.signOut}>
                                <NavLink to={`/signout`}>Sign out</NavLink>
                            </div>
                        </div>
                        <div className={classes.accountCard}>
                            <AccountToggle expanded={true} mobile />
                        </div>
                        {isFenix ? (
                            <Prism featureName="OnBehalfOfUser" fallback={<></>}>
                                <div className={`${classes.accountCard} ${classes.second}`}>
                                    <OnBehalfOfUserToggle expanded={true} mobile />
                                </div>
                            </Prism>
                        ) : null}
                        {renderMenuItem({ path: '/payments/new', Component: <Payments />, label: 'Make a payment' })}
                        {/* Hiding until I create a proper reimbursement flow */}
                        {/* {renderMenuItem({ path: '/expenses/open/new', Component:  <Reimbursement />, label: 'New reimbursement' })} */}
                        {renderMenuItem({
                            path: '/bills/drafts/upload',
                            Component: <Bills />,
                            label: 'Upload a bill to pay',
                        })}
                        {getPermissions()?.permissions?.cards?.manage
                            ? renderMenuItem({
                                  path: `${cardsHomeUrl}/active`,
                                  Component: <Card />,
                                  label: 'Cards overview',
                              })
                            : null}
                        {renderMenuItem({ path: '/dashboard/deposit', Component: <Deposit />, label: 'Deposit funds' })}
                        {renderMenuItem({
                            path: `/accounts/convert/${baseCurrency}/${baseCurrency === 'USD' ? 'EUR' : 'USD'}`,
                            Component: <Conversion />,
                            label: 'Convert currency',
                        })}
                        {renderMenuItem({ path: '/accounts', Component: <Reporting />, label: 'View transactions' })}
                        {renderMenuItem({ path: '/setup/user', Component: <Settings />, label: 'User settings' })}
                        {renderMenuItem({ path: '/team/invite', Component: <Team />, label: 'Invite a new user' })}
                        {renderMenuItem({ path: '/refer', Component: <Gift />, label: 'Refer and earn' })}
                        <div className={classes.menuItem}>
                            <a
                                href={`https://knowledge.paytron.com/`}
                                target="_blank"
                                className={`${classes.menuLink}`}
                            >
                                <div className={classes.menuImage}>
                                    <Help />
                                </div>
                                Get some help
                            </a>
                        </div>
                        {isMobileApp && (
                            <div className={`${classes.menuItem} ${classes.menuLink} ${classes.appVersion}`}>
                                App version {appVersion}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );

    return (
        <div className={classes.hideMobile}>
            <div className={isActive ? classes.backgroundOpen : ''} onClick={toggleClass}></div>
            <div className={`${classes.mobileMenu} ${isActive ? classes.fullMenuOpen : ''}`}>
                {companyManager ? (
                    <>
                        {renderNavLink({ path: '/dashboard', Component: <Dashboard />, title: 'Dashboard' })}
                        {renderNavLink({
                            path: '/approvals/pending',
                            Component: <Approvals />,
                            title: 'Approvals',
                        })}
                        {renderNavLink({
                            path: `${cardsHomeUrl}/user/${getCurrentUserId()}`,
                            Component: <Card />,
                            title: 'Cards',
                        })}
                        {renderNavLink({ path: '/expenses/open', Component: <Expenses />, title: 'Expenses' })}
                        {renderNavLink({ path: `/budgets/active`, Component: <Budget />, title: 'Budgets' })}
                    </>
                ) : (
                    <>
                        {renderNavLink({
                            path: `${cardsHomeUrl}/user/${getCurrentUserId()}`,
                            Component: <Card />,
                            title: 'Cards',
                        })}
                        {renderNavLink({ path: '/expenses/open', Component: <Expenses />, title: 'Expenses' })}
                        {renderNavLink({ path: `/budgets/active`, Component: <Budget />, title: 'Budgets' })}
                        {renderNavLink({
                            path: '/approvals/pending',
                            Component: <Approvals />,
                            title: 'Approvals',
                        })}
                    </>
                )}
                <div className={classes.mLink}></div>
            </div>
            <div className={`${classes.menu} ${isActive ? classes.open : ''}`}>
                <div className={`${classes.menuOpenButton} `} onClick={toggleClass}>
                    <span className={`${classes.lines} ${classes.line1}`}></span>
                    <span className={`${classes.lines} ${classes.line2}`}></span>
                    <span className={`${classes.lines} ${classes.line3}`}></span>
                </div>
                <div className={classes.menuItems}>
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                        <PaytronLogo fill="#123688" width={'100px'} style={{ marginLeft: '20px' }} />
                        <div className={classes.signOut}>
                            <NavLink to={`/signout`}>Sign out</NavLink>
                        </div>
                    </div>
                    <div className={classes.accountCard}>
                        <AccountToggle expanded={true} mobile />
                    </div>
                    {isFenix ? (
                        <Prism featureName="OnBehalfOfUser" fallback={<></>}>
                            <div className={`${classes.accountCard} ${classes.second}`}>
                                <OnBehalfOfUserToggle expanded={true} mobile />
                            </div>
                        </Prism>
                    ) : null}
                    {renderMenuItem({ path: '/expenses/open/new', Component: <Camera />, label: 'Capture a receipt' })}
                    {renderMenuItem({ path: '/payments/new', Component: <Payments />, label: 'Make a payment' })}
                    {/* Hiding until I create a proper reimbursement flow */}
                    {/* {renderMenuItem({ path: '/expenses/open/new', Component:  <Reimbursement />, label: 'New reimbursement' })} */}
                    {renderMenuItem({
                        path: '/bills/drafts/upload',
                        Component: <Bills />,
                        label: 'Upload a bill to pay',
                    })}
                    {getPermissions()?.permissions?.cards?.manage
                        ? renderMenuItem({
                              path: `${cardsHomeUrl}/active`,
                              Component: <Card />,
                              label: 'Cards overview',
                          })
                        : null}
                    {renderMenuItem({ path: '/dashboard/deposit', Component: <Deposit />, label: 'Deposit funds' })}
                    {renderMenuItem({
                        path: `/accounts/convert/${baseCurrency}/${baseCurrency === 'USD' ? 'EUR' : 'USD'}`,
                        Component: <Conversion />,
                        label: 'Convert currency',
                    })}
                    {renderMenuItem({ path: '/accounts', Component: <Reporting />, label: 'View transactions' })}
                    {renderMenuItem({ path: '/setup/user', Component: <Settings />, label: 'User settings' })}
                    {renderMenuItem({ path: '/team/invite', Component: <Team />, label: 'Invite a new user' })}
                    {renderMenuItem({ path: '/refer', Component: <Gift />, label: 'Refer and earn' })}
                    <div className={classes.menuItem}>
                        <a href={`https://knowledge.paytron.com/`} target="_blank" className={`${classes.menuLink}`}>
                            <div className={classes.menuImage}>
                                <Help />
                            </div>
                            Get some help
                        </a>
                    </div>
                    {isMobileApp && (
                        <div className={`${classes.menuItem} ${classes.menuLink} ${classes.appVersion}`}>
                            App version {appVersion}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MobileMenu;
